/**
 * Lifecycle windows — every time-based threshold in the connection lifecycle,
 * in ONE place (user request, device pass 12: the numbers were scattered
 * across three files and could drift; copy strings derive from these so
 * behavior and words can't disagree).
 *
 * | Constant                 | Value | Meaning                                                      |
 * |--------------------------|-------|--------------------------------------------------------------|
 * | SOFT_INACTIVE_MS         | 24h   | Profile's view of an Anon: "soft inactive" (quiet hint)      |
 * | ANON_CHAT_EXPIRY_MS      | 7d    | Anon chat expiry — the offer-to-forget window (Anon's view;  |
 * |                          |       | rolling from last activity; signup keeps the chat, #23)      |
 * | INACTIVE_MS              | 7d    | Profile's view of an Anon: inactive, chat button greys       |
 * | ACTIVITY_STATUS_HIDE_DAYS| 14d   | "Active X days ago" stops displaying — silence is the signal |
 *
 * The 3d/7d tension is RESOLVED (plan 3 D21, 2026-08-15): the Anon chat window
 * moved 3d → 7d, so it now matches the Profile side's inactive threshold and
 * the days-3-to-7 dead zone is gone — a Profile can no longer message into a
 * chat the Anon can't see while still being shown as active.
 *
 * Why it moved: "Remind me later" (plan 3) sets a calendar reminder for the
 * next morning carrying a link back into the room, and at a 3-day window a
 * reminder could land on ExpiredChatScreen — the feature would have defeated
 * itself. Fixed by moving the number rather than adding a per-room exemption
 * (user ruling): one constant, no conditional logic, and the wider window
 * benefits everyone rather than only people who tapped the reminder.
 *
 * Expiry is a VIEW gate, never deletion (CLAUDE-PROJECT § Core Guarantees), so
 * widening it makes previously-expired rooms render again — expected, not a
 * migration. Nothing stored changes.
 */

// Toggle for testing: true = 30s / 2min inactive thresholds and a 2-minute
// anon chat expiry. Branch deploys only — never reaches main (revert before
// merge). Exported so tests can skip derivation assertions while active.
export const DEBUG_TIMERS = false;

// Session-adoption bound: a QR session whose heartbeat stopped more than this
// long ago cannot be scanned into (getActiveSession rejects it; the prefetch
// fast path enforces the SAME bound inline in public/index.html, which cannot
// import this constant — change both together). Deliberately NOT on
// DEBUG_TIMERS: the prefetch twin can't follow a debug value, and a split
// bound would break scanning on branch deploys.
export const SESSION_FRESHNESS_MS = 5 * 60 * 1000;

export const SOFT_INACTIVE_MS = DEBUG_TIMERS ? 30 * 1000 : 24 * 60 * 60 * 1000;
export const INACTIVE_MS = DEBUG_TIMERS ? 2 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;

export const ANON_CHAT_EXPIRY_DAYS = 7;
export const ANON_CHAT_EXPIRY_MS = DEBUG_TIMERS
  ? 2 * 60 * 1000
  : ANON_CHAT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

// D8 (exchange-clarity, exploring): expiry-line copy variants for the device
// feel pass — "in 3 days" / "in one day" / "tomorrow". The 'days' variant
// derives from ANON_CHAT_EXPIRY_DAYS above; 'oneDay'/'tomorrow' are feel-test
// phrasings that are only TRUE with a 1-day window — if one wins, the
// functionality updates to make the line true (ANON_CHAT_EXPIRY_DAYS → 1),
// never the words drifting from the window. One home with the constant so
// the confrontation is unavoidable.
export const EXPIRY_COPY_VARIANT = 'days'; // 'days' | 'oneDay' | 'tomorrow'

export const anonChatExpiryPhrase = () => {
  switch (EXPIRY_COPY_VARIANT) {
    case 'oneDay':
      return 'in one day';
    case 'tomorrow':
      return 'tomorrow';
    default:
      return `in ${ANON_CHAT_EXPIRY_DAYS} days`;
  }
};

export const ACTIVITY_STATUS_HIDE_DAYS = 14;
